// Static content for Nova SecureNet — single source of truth.
import {
  Globe, ShieldCheck, ServerCog, LifeBuoy, Sparkles, Rocket,
  Layout, Search, Gauge, Mail, Cloud, Network, Database, Users,
  PenTool, Code2, ClipboardCheck, Send, MessagesSquare, RefreshCw,
} from "lucide-react";

export const NAV_LINKS = [
  { label: "Home", to: "/" },
  { label: "Services", to: "/services" },
  { label: "Website Packages", to: "/packages" },
  { label: "Portfolio", to: "/portfolio" },
  { label: "About", to: "/about" },
  { label: "Contact", to: "/contact" },
];

export const WHY_CHOOSE = [
  {
    icon: Globe,
    title: "Modern Website Design",
    desc: "Fast, responsive websites built to convert visitors into customers — designed around your brand, not a template.",
  },
  {
    icon: ShieldCheck,
    title: "Digital Security",
    desc: "Practical, jargon-free protection for your website and email so your business stays safe and trusted online.",
  },
  {
    icon: ServerCog,
    title: "Managed IT Solutions",
    desc: "Microsoft 365, Google Workspace, domains and hosting — set up and managed correctly the first time.",
  },
  {
    icon: LifeBuoy,
    title: "Ongoing Support",
    desc: "A dependable long-term partner who answers when you call and keeps everything running smoothly.",
  },
];

export const FEATURED_SERVICES = [
  {
    id: "web",
    icon: Globe,
    title: "Website Development",
    tagline: "Professional websites that work as hard as you do.",
    points: ["Professional business websites", "Landing pages", "Responsive design", "SEO-ready builds", "Fast performance", "Modern user experience"],
    image: "https://images.unsplash.com/photo-1542831371-29b0f74f9713?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
  },
  {
    id: "security",
    icon: ShieldCheck,
    title: "Digital Security",
    tagline: "Approachable protection, without the fear tactics.",
    points: ["Website hardening", "Security reviews", "Email protection", "Backup planning", "Business technology guidance"],
    image: "https://images.unsplash.com/photo-1768839721176-2fa91fdce725?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
  },
  {
    id: "it",
    icon: ServerCog,
    title: "Trusted IT Solutions",
    tagline: "The technology backbone of your business, handled.",
    points: ["Microsoft 365 setup", "Business email", "Google Workspace", "Cloudflare", "Domain management", "Hosting management", "Technology consulting"],
    image: "https://images.unsplash.com/photo-1579389082978-de4aece6f446?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
  },
];

export const SERVICE_CATEGORIES = [
  {
    id: "web",
    icon: Globe,
    title: "Website Development",
    blurb: "Beautiful, fast, and built to grow with your business.",
    items: [
      "Business websites", "Landing pages", "Portfolio websites", "Restaurant websites",
      "Service business websites", "Event websites", "Website redesigns", "Website modernization",
    ],
  },
  {
    id: "security",
    icon: ShieldCheck,
    title: "Digital Security",
    blurb: "Straightforward protection for your website, email, and data.",
    items: [
      "Website security reviews", "Business security assessments", "Website hardening",
      "Backup planning", "Email protection", "Security best practices", "Technology consulting",
    ],
  },
  {
    id: "it",
    icon: ServerCog,
    title: "Trusted IT Solutions",
    blurb: "Reliable systems set up and managed correctly.",
    items: [
      "Microsoft 365", "Google Workspace", "Business email", "Domain registration", "DNS management",
      "Cloudflare configuration", "Website migrations", "Hosting migrations", "General IT consulting",
    ],
  },
];

export const PACKAGES = [
  {
    id: "launch",
    name: "Launch",
    price: "$500",
    currency: "CAD",
    tagline: "A professional online presence, fast.",
    popular: false,
    features: ["One-page website", "Responsive design", "Contact form", "Google Maps", "Social links", "Basic SEO", "One revision", "≈ one week delivery"],
  },
  {
    id: "foundation",
    name: "Foundation",
    price: "$900",
    currency: "CAD",
    tagline: "A complete website for small business.",
    popular: false,
    features: ["Up to five pages", "Contact forms", "Photo gallery", "Google Business integration", "Basic SEO", "Two revisions", "≈ two weeks delivery"],
  },
  {
    id: "growth",
    name: "Growth",
    price: "$1,500",
    currency: "CAD",
    tagline: "Built for growing businesses.",
    popular: true,
    features: ["Everything in Foundation", "Up to ten pages", "Blog capability", "Enhanced SEO", "Google Analytics", "Performance optimization", "Three revisions", "Priority launch support"],
  },
  {
    id: "signature",
    name: "Signature",
    price: "From $2,500",
    currency: "CAD",
    tagline: "Custom business solutions.",
    popular: false,
    custom: true,
    features: ["Client portals", "Booking systems", "Membership websites", "Dashboards", "Custom software", "API integrations", "Admin systems"],
  },
];

export const CARE_PLANS = [
  {
    id: "build",
    name: "Build Only",
    price: "One-time",
    tagline: "You own it, fully. No ongoing commitment.",
    features: ["One-time project fee", "Client owns the website", "Choose your own hosting", "No ongoing commitment"],
  },
  {
    id: "starter",
    name: "Starter Care",
    price: "$50",
    per: "/month",
    tagline: "The essentials, handled.",
    features: ["Hosting", "Software updates", "Routine backups"],
  },
  {
    id: "business",
    name: "Business Care",
    price: "$100",
    per: "/month",
    popular: true,
    tagline: "Everything in Starter, plus care.",
    features: ["Everything in Starter", "Priority support", "Monthly maintenance", "Security reviews"],
  },
  {
    id: "premium",
    name: "Premium Care",
    price: "$150",
    per: "/month",
    tagline: "Everything in Business, elevated.",
    features: ["Everything in Business", "Performance optimization", "Monthly content updates", "Advanced monitoring", "Faster response times"],
  },
];

export const CARE_MATRIX = {
  features: [
    "Hosting", "Software updates", "Routine backups", "Priority support",
    "Monthly maintenance", "Security reviews", "Performance optimization",
    "Monthly content updates", "Advanced monitoring", "Faster response times",
  ],
  plans: {
    "Build Only": [false, false, false, false, false, false, false, false, false, false],
    "Starter Care": [true, true, true, false, false, false, false, false, false, false],
    "Business Care": [true, true, true, true, true, true, false, false, false, false],
    "Premium Care": [true, true, true, true, true, true, true, true, true, true],
  },
};

export const PROCESS_STEPS = [
  { icon: MessagesSquare, title: "Discovery Consultation", desc: "We learn about your business, goals, and what success looks like for you." },
  { icon: ClipboardCheck, title: "Proposal & Planning", desc: "A clear plan, timeline, and fixed pricing — no surprises." },
  { icon: PenTool, title: "Design", desc: "We craft a modern look that reflects your brand and earns trust." },
  { icon: Code2, title: "Development", desc: "We build fast, secure, and responsive — ready for every device." },
  { icon: RefreshCw, title: "Review & Revisions", desc: "You review, we refine. We get the details right together." },
  { icon: Rocket, title: "Launch", desc: "We handle the technical launch so everything goes live smoothly." },
  { icon: LifeBuoy, title: "Optional Ongoing Support", desc: "Choose a care plan and we keep everything running long-term." },
];

export const PORTFOLIO = [
  {
    id: "ams",
    name: "AMS Venture Holdings",
    category: "Corporate · Investment",
    desc: "A refined corporate presence for an investment holdings group, built to project stability and trust.",
    tech: ["React", "SEO", "Responsive"],
    outcome: "Clear brand credibility and a professional first impression for partners.",
    image: "https://images.unsplash.com/photo-1634084462412-b54873c0a56d?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
  },
  {
    id: "dragon",
    name: "Dead Dragon Entertainment",
    category: "Entertainment · Media",
    desc: "A bold, immersive site for an entertainment brand with rich media and striking visuals.",
    tech: ["Motion", "Media", "Performance"],
    outcome: "A memorable brand experience that stands out in a crowded space.",
    image: "https://images.unsplash.com/photo-1686061592689-312bbfb5c055?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
  },
  {
    id: "rebel",
    name: "Rebel Lash",
    category: "Beauty · Local Business",
    desc: "A conversion-focused website for a beauty studio with online booking and gallery.",
    tech: ["Booking", "Gallery", "Local SEO"],
    outcome: "More bookings and a polished storefront that reflects the brand.",
    image: "https://images.unsplash.com/photo-1551288049-bebda4e38f71?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
  },
  {
    id: "future",
    name: "Future Client",
    category: "Your project",
    desc: "This space is reserved for your business. Let's build something you're proud of.",
    tech: ["Discovery", "Design", "Launch"],
    outcome: "Your success story, coming soon.",
    image: "https://images.unsplash.com/photo-1497366216548-37526070297c?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
    placeholder: true,
  },
];

export const TESTIMONIALS = [
  {
    quote: "Nova SecureNet made the whole process simple. They explained everything in plain language and delivered a website that finally feels like us.",
    name: "Jordan Mills",
    role: "Owner, Northline Contracting",
  },
  {
    quote: "Reliable, responsive, and genuinely helpful. Our email and hosting issues disappeared and we haven't looked back.",
    name: "Priya Anand",
    role: "Director, Maple Grove Clinic",
  },
  {
    quote: "They treated our small business like it mattered. The site is fast, looks premium, and brings in real leads.",
    name: "Marc Tremblay",
    role: "Founder, Atelier Nord",
  },
];

export const SERVICE_OPTIONS = [
  "Website Development", "Website Redesign", "Digital Security", "Microsoft 365 / Google Workspace",
  "Domain & DNS / Cloudflare", "Hosting & Care Plan", "Custom / Signature Project", "Not sure yet",
];

export const BUDGET_OPTIONS = [
  "Under $500", "$500 – $900", "$900 – $1,500", "$1,500 – $2,500", "$2,500+", "Not sure yet",
];

export const TIMELINE_OPTIONS = [
  "As soon as possible", "1–2 weeks", "1 month", "1–3 months", "Just exploring",
];

export const CONTACT_METHODS = ["Email", "Phone", "Either"];

export const ICONS = { Sparkles, Layout, Search, Gauge, Mail, Cloud, Network, Database, Users };
